import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter } from 'recharts';
import { useProductionData } from '../../contexts/ProductionDataContext';

const MiningProductionData = ({ objectName, objectType }) => {
  const { getDevelopmentProduction, getStopingProduction, loading: dataLoading } = useProductionData();
  const [miningReturnsData, setMiningReturnsData] = useState([]);
  const [productionReturnsData, setProductionReturnsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productionLoading, setProductionLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [productionCurrentPage, setProductionCurrentPage] = useState(1);
  const [productionItemsPerPage] = useState(10);
  const [xAxis, setXAxis] = useState('DateDeveloped');
  const [yAxis, setYAxis] = useState('HolesDrilled');
  const [chartType, setChartType] = useState('bar');
  const [productionXAxis, setProductionXAxis] = useState('DateDeveloped');
  const [productionYAxis, setProductionYAxis] = useState('HolesDrilled');
  const [productionChartType, setProductionChartType] = useState('bar');

  // Map new development drilling column names to old format for compatibility
  const mapDevelopmentData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => ({
      StopeID: item.dev_end_id || item.stope_id || null, // Use dev_end_id for development
      EndID: item.dev_end_id || null,
      DateDeveloped: item.advance_date || null,
      HolesDrilled: item.holes_drilled_num || 0,
      HolesCharged: item.holes_charged_num || 0,
      HolesDepth: item.hole_depth_m || 0,
      ExplosivesEmex: item.emex_sticks_num || 0,
      ExplosivesEZStoppers: item.shock_tubes_num || 0,
      ExplosivesIED: item.ied_num || 0,
      ExplosivesIGCord: item.gcord_m || 0,
      PreviousAdvance: item.advance_m || 0,
      EstimatedExplosives: item.est_explosives_kg || 0,
      EstimatedTonnage: item.est_tonnage_t || 0,
      EstimatedPowderFactor: item.est_powder_factor || 0,
      MachineOperator: item.machine_oper_id || null,
      MachineAssistant: item.machine_asst_id || null,
      TeamLeader: item.team_leader_id || null,
      Comments: item.comments || null,
      Planned: item.planned || null,
      Month: item.advance_date ? (() => {
        const date = new Date(item.advance_date);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}_${year}`;
        }
        return null;
      })() : null,
      // Keep original fields for reference
      prd_id: item.prd_id,
      level_id: item.level_id,
      advance_m: item.advance_m
    }));
  };

  // Map new production drilling column names to old format for compatibility
  const mapProductionData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => ({
      STOPEID: item.stope_id || null,
      StopeID: item.stope_id || null, // Also map to StopeID for filtering
      DateDeveloped: item.drilling_date || null,
      HolesDrilled: item.holes_drilled_num || 0,
      HolesCharged: item.holes_charged_num || 0,
      HolesDepth: item.hole_depth_m || 0,
      ExplosivesEmex: item.emex_sticks_num || 0,
      ExplosivesEZStoppers: item.shock_tubes_num || 0,
      ExplosivesIED: item.ied_num || 0,
      ExplosivesIGCord: item.gcord_length_m || 0,
      BenchLength: item.bench_length_m || 0,
      BenchWidth: item.bench_width_m || 0,
      EstimatedTonnage: item.est_tonnage_t || 0,
      EstimatedPowderFactor: item.est_powder_factor || 0,
      EstimatedExplosives: item.est_explosives_kg || 0,
      BenchArea: item.bench_area_m2 || 0,
      TonnesBroken: item.tonnes_broken || 0,
      MachineOperator: item.machine_oper_id || null,
      MachineAssistant: item.machine_asst_id || null,
      TeamLeader: item.team_leader_id || null,
      Comments: item.comments || null,
      Planned: item.planned || null,
      Month: item.drilling_date ? (() => {
        const date = new Date(item.drilling_date);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}_${year}`;
        }
        return null;
      })() : null,
      // Keep original fields for reference
      prd_id: item.prd_id,
      level_id: item.level_id
    }));
  };

  // Pagination logic
  const totalPages = Math.ceil(miningReturnsData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = miningReturnsData.slice(startIndex, endIndex);

  // Production Returns pagination logic
  const productionTotalPages = Math.ceil(productionReturnsData.length / productionItemsPerPage);
  const productionStartIndex = (productionCurrentPage - 1) * productionItemsPerPage;
  const productionEndIndex = productionStartIndex + productionItemsPerPage;
  const productionCurrentData = productionReturnsData.slice(productionStartIndex, productionEndIndex);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [miningReturnsData]);

  useEffect(() => {
    setProductionCurrentPage(1);
  }, [productionReturnsData]);

  // Format date for better readability
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Get all columns for X axis
  const getAllColumns = () => {
    if (miningReturnsData.length === 0) return [];
    const excludedFields = ['StopeID', 'EndID', 'HolesDrilled', 'HolesCharged', 'HolesDepth', 
                            'ExplosivesEmex', 'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                            'PreviousAdvance', 'EstimatedExplosives', 'EstimatedTonnage', 'EstimatedPowderFactor'];
    const availableFields = Object.keys(miningReturnsData[0]).filter(field => !excludedFields.includes(field));
    
    const preferredOrder = [
      'DateDeveloped',
      'Month',
      'MachineOperator',
      'MachineAssistant', 
      'TeamLeader',
      'Comments',
      'Planned'
    ];
    
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Always add Month as it's a computed field
    if (!orderedFields.includes('Month')) {
      orderedFields.splice(1, 0, 'Month'); // Insert after DateDeveloped
    }
    
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Get numeric columns for Y axis
  const getNumericColumns = () => {
    const numericFields = ['HolesDrilled', 'HolesCharged', 'HolesDepth', 'ExplosivesEmex', 
                           'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                           'PreviousAdvance', 'EstimatedExplosives', 'EstimatedTonnage', 'EstimatedPowderFactor'];
    return numericFields.filter(field => miningReturnsData.some(item => !isNaN(parseFloat(item[field]))));
  };

  // Get all columns for Production Returns X axis
  const getProductionAllColumns = () => {
    if (productionReturnsData.length === 0) return [];
    const excludedFields = ['STOPEID', 'HolesDrilled', 'HolesCharged', 'HolesDepth', 
                            'ExplosivesEmex', 'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                            'BenchLength', 'BenchWidth', 'EstimatedTonnage', 'EstimatedPowderFactor', 
                            'EstimatedExplosives', 'BenchArea', 'TonnesBroken'];
    const availableFields = Object.keys(productionReturnsData[0]).filter(field => !excludedFields.includes(field));
    
    const preferredOrder = [
      'DateDeveloped',
      'Month',
      'MachineOperator',
      'MachineAssistant', 
      'TeamLeader',
      'Comments',
      'Planned'
    ];
    
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Always add Month as it's a computed field
    if (!orderedFields.includes('Month')) {
      orderedFields.splice(1, 0, 'Month'); // Insert after DateDeveloped
    }
    
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Get numeric columns for Production Returns Y axis
  const getProductionNumericColumns = () => {
    const numericFields = ['HolesDrilled', 'HolesCharged', 'HolesDepth', 'ExplosivesEmex', 
                           'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                           'BenchLength', 'BenchWidth', 'EstimatedTonnage', 'EstimatedPowderFactor', 
                           'EstimatedExplosives', 'BenchArea', 'TonnesBroken'];
    return numericFields.filter(field => productionReturnsData.some(item => !isNaN(parseFloat(item[field]))));
  };

  // Process data for chart
  const processChartData = () => {
    if (!miningReturnsData.length) return [];

    const grouped = {};
    const originalDates = {};
    
    miningReturnsData.forEach(item => {
      let key = item[xAxis];
      let originalDate = null;
      
      if (xAxis === 'DateDeveloped') {
        originalDate = item[xAxis];
        key = formatDate(key);
      } else if (xAxis === 'Month') {
        // Create Month field from DateDeveloped
        const date = new Date(item.DateDeveloped);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [xAxis]: key,
          count: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      getNumericColumns().forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    const processedData = Object.values(grouped).map(group => {
      const processed = { [xAxis]: group[xAxis], count: group.count };
      
      getNumericColumns().forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Use sum instead of average
          processed[field] = parseFloat(total.toFixed(2));
          processed[`${field}_total`] = total;
        }
      });
      
      return processed;
    });

    if (xAxis === 'DateDeveloped') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[xAxis]]);
        const dateB = new Date(originalDates[b[xAxis]]);
        return dateA - dateB;
      });
    }

    if (xAxis === 'Month') {
      return processedData.sort((a, b) => {
        // Sort by year first, then by month
        const [monthA, yearA] = a[xAxis].split('_');
        const [monthB, yearB] = b[xAxis].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const chartData = processChartData();

  // Process data for Production Returns chart
  const processProductionChartData = () => {
    if (!productionReturnsData.length) return [];

    const grouped = {};
    const originalDates = {};
    
    productionReturnsData.forEach(item => {
      let key = item[productionXAxis];
      let originalDate = null;
      
      if (productionXAxis === 'DateDeveloped') {
        originalDate = item[productionXAxis];
        key = formatDate(key);
      } else if (productionXAxis === 'Month') {
        // Create Month field from DateDeveloped
        const date = new Date(item.DateDeveloped);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [productionXAxis]: key,
          count: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      getProductionNumericColumns().forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    const processedData = Object.values(grouped).map(group => {
      const processed = { [productionXAxis]: group[productionXAxis], count: group.count };
      
      getProductionNumericColumns().forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Use sum instead of average
          processed[field] = parseFloat(total.toFixed(2));
          processed[`${field}_total`] = total;
        }
      });
      
      return processed;
    });

    if (productionXAxis === 'DateDeveloped') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[productionXAxis]]);
        const dateB = new Date(originalDates[b[productionXAxis]]);
        return dateA - dateB;
      });
    }

    if (productionXAxis === 'Month') {
      return processedData.sort((a, b) => {
        // Sort by year first, then by month
        const [monthA, yearA] = a[productionXAxis].split('_');
        const [monthB, yearB] = b[productionXAxis].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const productionChartData = processProductionChartData();

  // Filter mining returns data by objectName
  // For development drilling, we filter by dev_end_id (mapped to objectName)
  // For production drilling, we filter by stope_id
  const filterMiningReturnsByStope = (data, isProduction = false) => {
    if (!objectName || !data.length) return data;
    
    return data.filter(item => {
      // For development drilling, use dev_end_id or EndID
      // For production drilling, use stope_id or STOPEID
      const databaseID = isProduction 
        ? (item.stope_id || item.STOPEID || item.StopeID)
        : (item.dev_end_id || item.EndID || item.StopeID);
      
      if (!databaseID) return false;
      
      const parameterObjectName = objectName;
      
      // Check if database ID is at least 5 characters long and contains "L_"
      if (databaseID.length < 5 || !databaseID.includes('L_')) {
        return false;
      }
      
      if (parameterObjectName.startsWith(databaseID)) {
        const nextChar = parameterObjectName.charAt(databaseID.length);
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  // Get raw data from ProductionDataHandler
  const rawDevelopmentData = getDevelopmentProduction();
  const rawProductionData = getStopingProduction();

  // Use refs to track previous data lengths to prevent infinite loops
  const prevDevDataLengthRef = useRef(0);
  const prevProdDataLengthRef = useRef(0);
  const prevObjectNameRef = useRef(objectName);

  // Process and filter data when objectName or raw data changes
  useEffect(() => {
    // Check if objectName changed or data lengths changed
    const objectNameChanged = prevObjectNameRef.current !== objectName;
    const devDataChanged = prevDevDataLengthRef.current !== rawDevelopmentData.length;
    const prodDataChanged = prevProdDataLengthRef.current !== rawProductionData.length;

    if (!objectNameChanged && !devDataChanged && !prodDataChanged) {
      // Nothing changed, skip processing
      return;
    }

    // Update refs
    prevObjectNameRef.current = objectName;
    prevDevDataLengthRef.current = rawDevelopmentData.length;
    prevProdDataLengthRef.current = rawProductionData.length;

    if (!objectName) {
      setMiningReturnsData([]);
      setProductionReturnsData([]);
      return;
    }

    setLoading(true);
    setProductionLoading(true);

    try {
      // Process development drilling data
      const filteredDevelopmentData = filterMiningReturnsByStope(rawDevelopmentData, false);
      const mappedDevelopmentData = mapDevelopmentData(filteredDevelopmentData);
      
      console.log('[MiningReturns] Development drilling - Original data count:', rawDevelopmentData.length);
      console.log('[MiningReturns] Development drilling - Filtered data count:', filteredDevelopmentData.length);
      console.log('[MiningReturns] Development drilling - Mapped data count:', mappedDevelopmentData.length);
      
      setMiningReturnsData(mappedDevelopmentData);
      setLoading(false);

      // Process production drilling data
      const filteredProductionData = filterMiningReturnsByStope(rawProductionData, true);
      const mappedProductionData = mapProductionData(filteredProductionData);
      
      console.log('[MiningReturns] Production drilling - Original data count:', rawProductionData.length);
      console.log('[MiningReturns] Production drilling - Filtered data count:', filteredProductionData.length);
      console.log('[MiningReturns] Production drilling - Mapped data count:', mappedProductionData.length);
      
      setProductionReturnsData(mappedProductionData);
      setProductionLoading(false);
      setAuthError(false);
    } catch (err) {
      console.error('[MiningReturns] Error processing data:', err);
      setMiningReturnsData([]);
      setProductionReturnsData([]);
      setLoading(false);
      setProductionLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectName, objectType, rawDevelopmentData.length, rawProductionData.length]);

  if (authError) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-center font-semibold">Authentication Required</p>
          <p className="text-sm text-center mt-2">Please log in to access this data.</p>
        </div>
      </div>
    );
  }

  if (dataLoading || loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-2 text-zinc-400">
          {dataLoading ? 'Loading production data...' : 'Loading development drilling data...'}
        </span>
      </div>
    );
  }


  // Calculate summary statistics for each drilling type
  const calculateDrillingSummaryStats = (data, isProduction = false) => {
    if (!data || data.length === 0) {
      return {
        dateRange: 'N/A',
        holesDrilled: 0,
        holesCharged: 0,
        holesDepth: 0,
        explosivesEmex: 0,
        explosivesEZStoppers: 0,
        explosivesIED: 0,
        explosivesIGCord: 0,
        machineOperator: 0,
        benchLength: isProduction ? 0 : 'N/A',
        benchWidth: isProduction ? 0 : 'N/A',
        estimatedTonnage: 0,
        estimatedPowderFactor: 0,
        estimatedExplosives: 0,
        benchArea: isProduction ? 0 : 'N/A',
        tonnesBroken: isProduction ? 0 : 'N/A'
      };
    }

    const dates = data.map(item => new Date(item.DateDeveloped)).filter(date => !isNaN(date.getTime()));
    
    // Calculate sums for numeric fields
    const holesDrilled = data.reduce((sum, item) => sum + (parseFloat(item.HolesDrilled) || 0), 0);
    const holesCharged = data.reduce((sum, item) => sum + (parseFloat(item.HolesCharged) || 0), 0);
    const holesDepth = data.reduce((sum, item) => sum + (parseFloat(item.HolesDepth) || 0), 0);
    const explosivesEmex = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesEmex) || 0), 0);
    const explosivesEZStoppers = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesEZStoppers) || 0), 0);
    const explosivesIED = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesIED) || 0), 0);
    const explosivesIGCord = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesIGCord) || 0), 0);
    const estimatedTonnage = data.reduce((sum, item) => sum + (parseFloat(item.EstimatedTonnage) || 0), 0);
    const estimatedPowderFactor = data.reduce((sum, item) => sum + (parseFloat(item.EstimatedPowderFactor) || 0), 0);
    const estimatedExplosives = data.reduce((sum, item) => sum + (parseFloat(item.EstimatedExplosives) || 0), 0);
    
    // Production-specific fields
    const benchLength = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.BenchLength) || 0), 0) : 'N/A';
    const benchWidth = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.BenchWidth) || 0), 0) : 'N/A';
    const benchArea = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.BenchArea) || 0), 0) : 'N/A';
    const tonnesBroken = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.TonnesBroken) || 0), 0) : 'N/A';
    
    // Get count of unique machine operators
    const operators = [...new Set(data.map(item => item.MachineOperator).filter(op => op))];
    const machineOperator = operators.length;
    
    return {
      dateRange: dates.length > 0 ? `${formatDate(new Date(Math.min(...dates)))} to ${formatDate(new Date(Math.max(...dates)))}` : 'N/A',
      holesDrilled,
      holesCharged,
      holesDepth,
      explosivesEmex,
      explosivesEZStoppers,
      explosivesIED,
      explosivesIGCord,
      machineOperator,
      benchLength,
      benchWidth,
      estimatedTonnage,
      estimatedPowderFactor,
      estimatedExplosives,
      benchArea,
      tonnesBroken
    };
  };

  const developmentStats = calculateDrillingSummaryStats(miningReturnsData, false);
  const productionStats = calculateDrillingSummaryStats(productionReturnsData, true);

  return (
    <div className="space-y-4">
      {/* Summary Statistics Table */}
      <div className="bg-zinc-950 rounded-lg shadow-sm border border-yellow-600/25">
        <h3 className="text-sm font-semibold text-zinc-300 mb-2 p-3 border-b border-yellow-600/25">Summary Statistics</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-zinc-800 border border-yellow-600/25 text-xs">
            <thead className="bg-zinc-900/80">
              <tr>
                <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Metric</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Development Drilling</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">Production Drilling</th>
              </tr>
            </thead>
            <tbody className="bg-zinc-950 divide-y divide-zinc-800">
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Date Range</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.dateRange}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.dateRange}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Holes Drilled</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.holesDrilled}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.holesDrilled}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Holes Charged</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.holesCharged}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.holesCharged}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Holes Depth</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.holesDepth.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.holesDepth.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Explosives Emex</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.explosivesEmex.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.explosivesEmex.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Explosives EZ Stoppers</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.explosivesEZStoppers.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.explosivesEZStoppers.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Explosives IED</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.explosivesIED.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.explosivesIED.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Explosives IG Cord</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.explosivesIGCord.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.explosivesIGCord.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Machine Operator Count</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.machineOperator}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.machineOperator}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Bench Length</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.benchLength}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.benchLength.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Bench Width</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.benchWidth}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.benchWidth.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Estimated Tonnage</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.estimatedTonnage.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.estimatedTonnage.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Estimated Powder Factor</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.estimatedPowderFactor.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.estimatedPowderFactor.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Estimated Explosives</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.estimatedExplosives.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.estimatedExplosives.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Bench Area</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.benchArea}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.benchArea.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Tonnes Broken</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">{developmentStats.tonnesBroken}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">{productionStats.tonnesBroken.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Development Drilling Table */}
      {miningReturnsData && miningReturnsData.length > 0 && (
        <>
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-zinc-300 mb-2">Development Drilling Data</h3>
          
          <div className="bg-zinc-950 rounded-lg shadow-sm border border-yellow-600/25">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-800 border border-yellow-600/25 text-xs">
              <thead className="bg-zinc-900/80">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Date Developed</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">End ID</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Holes Drilled</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Holes Charged</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Holes Depth</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Machine Operator</th>
                </tr>
              </thead>
              <tbody className="bg-zinc-950 divide-y divide-zinc-800">
                {currentData.map((item, index) => (
                  <tr key={index} className="hover:bg-yellow-500/5">
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">
                      {formatDate(item.DateDeveloped)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.EndID || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.HolesDrilled || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.HolesCharged || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.HolesDepth || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100">{item.MachineOperator || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {miningReturnsData.length > itemsPerPage && (
            <div className="flex items-center justify-center space-x-2 p-2 border-t border-yellow-600/25 bg-zinc-900/80">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                title="First page"
              >
                |&lt;
              </button>
              
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                title="Previous page"
              >
                &lt;
              </button>
              
              <span className="text-xs text-zinc-400 px-2">
                {currentPage} / {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                title="Next page"
              >
                &gt;
              </button>
              
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                title="Last page"
              >
                &gt;|
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Interactive Graph Section */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-zinc-300 mb-2">Development Drilling Graph</h3>
          
          <div className="bg-zinc-950 rounded-lg shadow-sm border border-yellow-600/25 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">X Axis</label>
                <select 
                  value={xAxis} 
                  onChange={(e) => setXAxis(e.target.value)}
                  className="w-full p-2 border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 text-sm"
                >
                  {getAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Y Axis</label>
                <select 
                  value={yAxis} 
                  onChange={(e) => setYAxis(e.target.value)}
                  className="w-full p-2 border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 text-sm"
                >
                  <option value="count">Count</option>
                  {getNumericColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Chart Type</label>
                <select 
                  value={chartType} 
                  onChange={(e) => setChartType(e.target.value)}
                  className="w-full p-2 border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 text-sm"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setXAxis('DateDeveloped');
                    setYAxis('HolesDrilled');
                    setChartType('bar');
                  }}
                  className="w-full px-3 py-2 bg-zinc-900 text-zinc-300 rounded-md hover:bg-zinc-800 text-sm"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Chart Container */}
            <div style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bar' && (
                  <BarChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                      tick={{ fill: '#a1a1aa' }}
                      stroke="#52525b"
                    />
                    <YAxis fontSize={11} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                    <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#a1a1aa" }} />
                    <Bar dataKey={yAxis} fill="#eab308" />
                  </BarChart>
                )}
                
                {chartType === 'line' && (
                  <LineChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                      tick={{ fill: '#a1a1aa' }}
                      stroke="#52525b"
                    />
                    <YAxis fontSize={11} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                    <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#a1a1aa" }} />
                    <Line type="monotone" dataKey={yAxis} stroke="#eab308" strokeWidth={2} />
                  </LineChart>
                )}
                
                {chartType === 'scatter' && (
                  <ScatterChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                      tick={{ fill: '#a1a1aa' }}
                      stroke="#52525b"
                    />
                    <YAxis fontSize={11} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                    <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                    <Scatter dataKey={yAxis} fill="#eab308" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Production Drilling Section */}
      {productionReturnsData && productionReturnsData.length > 0 && (
        <>
        <div className="mt-8">
        <h3 className="text-sm font-semibold text-zinc-300 mb-2">Production Drilling Data</h3>
        
        {productionLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-2 text-zinc-400">Loading production drilling data...</span>
          </div>
        ) : productionReturnsData.length > 0 ? (
          <div className="bg-zinc-950 rounded-lg shadow-sm border border-yellow-600/25 overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-800 border border-yellow-600/25 text-xs">
              <thead className="bg-zinc-900/80">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Date</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">HolesDrilled</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">HolesCharged</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">HolesDepth</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">ExplosivesEmex</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">ExplosivesEZStoppers</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">ExplosivesIED</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">ExplosivesIGCord</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">MachineOperator</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">BenchLength</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">BenchWidth</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">EstimatedTonnage</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">EstimatedPowderFactor</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">EstimatedExplosives</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">BenchArea</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">TonnesBroken</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">MachineAssistant</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Comments</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Planned</th>
                </tr>
              </thead>
              <tbody className="bg-zinc-950 divide-y divide-zinc-800">
                {productionCurrentData.map((item, index) => (
                  <tr key={index} className="hover:bg-yellow-500/5">
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{formatDate(item.DateDeveloped)}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.HolesDrilled || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.HolesCharged || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.HolesDepth || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.ExplosivesEmex || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.ExplosivesEZStoppers || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.ExplosivesIED || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.ExplosivesIGCord || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.MachineOperator || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.BenchLength || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.BenchWidth || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.EstimatedTonnage || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.EstimatedPowderFactor || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.EstimatedExplosives || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.BenchArea || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.TonnesBroken || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.MachineAssistant || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 border-r border-yellow-600/25">{item.Comments || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100">{item.Planned || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Production Returns Pagination Controls */}
            {productionReturnsData.length > productionItemsPerPage && (
              <div className="flex items-center justify-center space-x-2 p-2 border-t border-yellow-600/25 bg-zinc-900/80">
                <button
                  onClick={() => setProductionCurrentPage(1)}
                  disabled={productionCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                  title="First page"
                >
                  |&lt;
                </button>
                
                <button
                  onClick={() => setProductionCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={productionCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                  title="Previous page"
                >
                  &lt;
                </button>
                
                <span className="text-xs text-zinc-400 px-2">
                  {productionCurrentPage} / {productionTotalPages}
                </span>
                
                <button
                  onClick={() => setProductionCurrentPage(prev => Math.min(prev + 1, productionTotalPages))}
                  disabled={productionCurrentPage === productionTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                  title="Next page"
                >
                  &gt;
                </button>
                
                <button
                  onClick={() => setProductionCurrentPage(productionTotalPages)}
                  disabled={productionCurrentPage === productionTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed border border-yellow-600/35 rounded"
                  title="Last page"
                >
                  &gt;|
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-zinc-500">
            <p>No production drilling data available for this stope.</p>
          </div>
        )}
      </div>

      {/* Production Drilling Graph */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-zinc-300 mb-2">Production Drilling Graph</h3>
          
          <div className="bg-zinc-950 rounded-lg shadow-sm border border-yellow-600/25 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-zinc-300 mb-1">X-Axis</label>
                <select
                  value={productionXAxis}
                  onChange={(e) => setProductionXAxis(e.target.value)}
                  className="w-full text-xs border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-2 py-1"
                >
                  {getProductionAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-zinc-300 mb-1">Y-Axis</label>
                <select
                  value={productionYAxis}
                  onChange={(e) => setProductionYAxis(e.target.value)}
                  className="w-full text-xs border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-2 py-1"
                >
                  {getProductionNumericColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-zinc-300 mb-1">Chart Type</label>
                <select
                  value={productionChartType}
                  onChange={(e) => setProductionChartType(e.target.value)}
                  className="w-full text-xs border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-2 py-1"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
            </div>

            {/* Chart */}
            <div className="w-full" style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {productionChartType === 'bar' && (
                  <BarChart 
                    data={productionChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                    <XAxis 
                      dataKey={productionXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                      tick={{ fill: '#a1a1aa' }}
                      stroke="#52525b"
                    />
                    <YAxis fontSize={11} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                    <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#a1a1aa" }} />
                    <Bar dataKey={productionYAxis} fill="#eab308" />
                  </BarChart>
                )}
                
                {productionChartType === 'line' && (
                  <LineChart 
                    data={productionChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                    <XAxis 
                      dataKey={productionXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                      tick={{ fill: '#a1a1aa' }}
                      stroke="#52525b"
                    />
                    <YAxis fontSize={11} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                    <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#a1a1aa" }} />
                    <Line type="monotone" dataKey={productionYAxis} stroke="#eab308" strokeWidth={2} />
                  </LineChart>
                )}
                
                {productionChartType === 'scatter' && (
                  <ScatterChart 
                    data={productionChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                    <XAxis 
                      dataKey={productionXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                      tick={{ fill: '#a1a1aa' }}
                      stroke="#52525b"
                    />
                    <YAxis fontSize={11} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                    <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                    <Scatter dataKey={productionYAxis} fill="#eab308" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default MiningProductionData;
